import {Box, Chip} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {FolioCellBox, FolioCellText} from "./FolioCellBox";
import {FolioAmountInput} from "./FolioCellInputs";
import {FolioFormat} from "./folioFormat";
import {priceOfCents} from "./serviceAutofill";

interface Props {
    line: FolioItem
    format: FolioFormat
    currency: string
    /** Nothing changes: a folio that can't change, a tip or an included line. */
    locked: boolean
    /** The reservation's own line keeps its saved price - unless it has none, then staff set one here. */
    primary: boolean
    error: boolean
    onChange: (patch: Partial<FolioItem>) => void
    onTouch: () => void
    onDiscount: () => void
}

/**
 * The unit price, always as the same box: the amount - or, without one, an empty box that says
 * "Set price" (red once its errors show) - then the discount chip. The amount stands alone like the
 * line's total, in the same figures; the column header names the currency ("Price (EUR)"). A price
 * set by hand is the service info's warning, not a mark here. A locked line (finalized, a guest link
 * out, a tip, an included part) is plain text: no outline, no chip to click.
 */
const FolioPriceCell = ({line, format, currency, locked, primary, error, onChange, onTouch, onDiscount}: Props) => {
    const {t} = useTranslation();
    const cents = line.price?.amountCents;
    // The reservation's line opens once it has no price and stays open, so the new price can be corrected
    const [unlocked, setUnlocked] = useState(false);
    if (primary && !locked && cents == null && !unlocked) setUnlocked(true);

    const editable = !locked && (!primary || unlocked);
    const discount = format.discount(line.discounts?.[0]);

    return (
        <FolioCellBox editable={editable} tone={error && !locked ? 'error' : 'default'} align={'right'} sx={{fontVariantNumeric: 'tabular-nums'}}>
            {editable ? (
                <FolioAmountInput
                    cents={cents}
                    onChange={next => onChange({price: priceOfCents(next, line.price?.currency || currency)})}
                    onBlur={onTouch}
                    format={format}
                    ariaLabel={t('Price')}
                    placeholder={t('Set price')}
                    error={error}
                />
            ) : (
                <FolioCellText value={cents != null ? format.amount(cents) : '—'}/>
            )}
            {!!discount && (locked ? (
                <Box component={'span'} title={`${t('Discount')} ${discount}`} sx={{flexShrink: 0, fontSize: 12, color: 'text.secondary'}}>
                    {discount}
                </Box>
            ) : (
                <Chip
                    size={'small'}
                    variant={'outlined'}
                    color={'secondary'}
                    label={discount}
                    onClick={onDiscount}
                    aria-label={`${t('Discount')} ${discount}`}
                    sx={{flexShrink: 0, height: 20, fontSize: 12, '& .MuiChip-label': {px: 0.75}}}
                />
            ))}
        </FolioCellBox>
    );
};

export default FolioPriceCell;
